/**
 * The mapping from the terrain feature bank's taxonomy to the referent
 * vocabulary. Ruling 26, 18 September 2026.
 *
 * Required, and never invented. Where a bank type maps to no referent it is
 * recorded unmapped; where a referent cannot be reached from terrain that is
 * recorded too. No mapping is invented to close a gap and no referent's
 * meaning is widened to make one fit.
 *
 * **The result is that the mapping is empty, and the reason is the same for
 * every entry.** The bank answers *what is on the ground*. Almost every
 * referent is *what the commander has decided about the ground* — an
 * objective, a rendezvous, a report line, a start point. A bridge is a
 * bridge whoever is fighting over it; it becomes an objective only because
 * someone said so, and the bank was never told.
 *
 * That is worth stating as a measured result rather than a shrug. It means
 * terrain cannot answer "where is the objective", and the design that has
 * Source B answering per-referent questions needs the assignment from
 * somewhere else — the sidecar, under Ruling 24.
 *
 * Only the bank's `Type` column is used. Feature names are the source
 * package's own proper names and do not enter this repository; a name-based
 * bridge would need a name index built outside it.
 */

import referentsJson from "../../../data/blank-referents.json";

/**
 * The bank's `Type` vocabulary, as emitted. Twenty-two category words,
 * counted across all 13,237 rows on 18 September 2026. Category words only:
 * no grid, no feature name, no coordinate.
 */
export const TERRAIN_TYPES = [
  "barrier",
  "bridge",
  "built-up",
  "causeway",
  "clearing",
  "cutting",
  "defile",
  "embankment",
  "ford",
  "gate",
  "marsh",
  "open_ground",
  "quarry",
  "railway",
  "range",
  "road",
  "spot_height",
  "structure",
  "track",
  "treeline",
  "turnaround",
  "water",
] as const;

export type TerrainType = (typeof TERRAIN_TYPES)[number];

/** Why a bank type reaches no referent. */
export type UnmappedReason =
  /** The ground exists; which one the orders mean is a decision, not a fact. */
  | "ASSIGNMENT_NOT_IN_SOURCE"
  /** No referent in the vocabulary names this kind of ground at all. */
  | "NO_REFERENT_NAMES_IT";

export interface TerrainTypeMapping {
  readonly because: string;
  /** Null wherever the bank type reaches no referent. */
  readonly referent: string | null;
  readonly type: TerrainType;
  readonly unmapped: UnmappedReason | null;
}

const ASSIGNED =
  "The bank records this as ground. Which piece of it an order means is assigned by the commander and is not in the source.";

const UNNAMED = "No term in the referent vocabulary names this kind of ground.";

/**
 * One entry per bank type. Every one of them is unmapped, and each says
 * which of the two reasons applies, because the two have different remedies:
 * an assignment can arrive from the sidecar, whereas a kind of ground the
 * vocabulary does not name would need the vocabulary changed, which is
 * Captain Holloway's to rule.
 */
export const TERRAIN_TYPE_MAPPINGS: readonly TerrainTypeMapping[] =
  Object.freeze([
    {
      type: "barrier",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "bridge",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "built-up",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "causeway",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "clearing",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "cutting",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "defile",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "embankment",
      referent: null,
      unmapped: "NO_REFERENT_NAMES_IT",
      because: UNNAMED,
    },
    {
      type: "ford",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "gate",
      referent: null,
      unmapped: "NO_REFERENT_NAMES_IT",
      because: UNNAMED,
    },
    {
      type: "marsh",
      referent: null,
      unmapped: "NO_REFERENT_NAMES_IT",
      because: UNNAMED,
    },
    {
      type: "open_ground",
      referent: null,
      unmapped: "NO_REFERENT_NAMES_IT",
      because: UNNAMED,
    },
    {
      type: "quarry",
      referent: null,
      unmapped: "NO_REFERENT_NAMES_IT",
      because: UNNAMED,
    },
    {
      type: "railway",
      referent: null,
      unmapped: "NO_REFERENT_NAMES_IT",
      because: UNNAMED,
    },
    {
      type: "range",
      referent: null,
      unmapped: "NO_REFERENT_NAMES_IT",
      because: UNNAMED,
    },
    {
      type: "road",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "spot_height",
      referent: null,
      unmapped: "NO_REFERENT_NAMES_IT",
      because: UNNAMED,
    },
    {
      type: "structure",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "track",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "treeline",
      referent: null,
      unmapped: "ASSIGNMENT_NOT_IN_SOURCE",
      because: ASSIGNED,
    },
    {
      type: "turnaround",
      referent: null,
      unmapped: "NO_REFERENT_NAMES_IT",
      because: UNNAMED,
    },
    {
      type: "water",
      referent: null,
      unmapped: "NO_REFERENT_NAMES_IT",
      because: UNNAMED,
    },
  ]);

/** Why a referent cannot be reached from the terrain bank. */
export type UnreachableReason =
  /** It names ground, but which ground is the commander's decision. */
  | "ASSIGNED_NOT_INTRINSIC"
  /** It is not a place at all — a time, a frequency, a callsign. */
  | "NOT_A_LOCATION";

export interface ReferentReachability {
  readonly because: string;
  readonly reachable: false;
  readonly referent: string;
  readonly unreachable: UnreachableReason;
}

const NOT_A_LOCATION_TERMS = new Set([
  "h-hour",
  "notice-to-move",
  "no-move-before",
  "frequency",
  "callsign",
]);

/**
 * One entry per referent in the vocabulary. Built from the vocabulary file
 * rather than typed out again, so a term added there cannot quietly acquire
 * an unexamined reachability.
 *
 * The split matters. A term that is not a location was never going to come
 * from a terrain bank and never will. A term that is assigned rather than
 * intrinsic *could* be reached, the day something carries the assignment —
 * which is exactly what the sidecar does under Ruling 24.
 */
export const REFERENT_REACHABILITY: readonly ReferentReachability[] =
  Object.freeze(
    referentsJson.referents.map(({ id }) =>
      NOT_A_LOCATION_TERMS.has(id)
        ? ({
            referent: id,
            reachable: false,
            unreachable: "NOT_A_LOCATION",
            because:
              "Not a place. No terrain source can carry a time, a frequency or a callsign.",
          } as const)
        : ({
            referent: id,
            reachable: false,
            unreachable: "ASSIGNED_NOT_INTRINSIC",
            because:
              "Names ground, but which ground is assigned per scenario. The bank records what is there, not what it has been made.",
          } as const),
    ),
  );

/**
 * The referent a bank type maps to, or null.
 *
 * Null for every type today. It is a function rather than a constant because
 * the answer is expected to change once the assignment has a source, and
 * every caller should already be asking rather than assuming.
 */
export function referentForTerrainType(type: string): string | null {
  return (
    TERRAIN_TYPE_MAPPINGS.find((entry) => entry.type === type)?.referent ?? null
  );
}

/** Bank types that reach no referent. Ruling 26's first list. */
export function unmappedTerrainTypes(): readonly TerrainTypeMapping[] {
  return TERRAIN_TYPE_MAPPINGS.filter((entry) => entry.referent === null);
}

/**
 * Referents that terrain cannot reach. Ruling 26's second list.
 *
 * Every entry, because the type says so: nothing in this file is reachable,
 * and a filter here would only pretend the question were still open.
 */
export function unreachableReferents(): readonly ReferentReachability[] {
  return REFERENT_REACHABILITY;
}
